import React from 'react';
import type { ChipColor } from '@/types/chip';
import type { AttendanceRecord } from '@/types/entities';
import type { StudentRecord, SessionRecord } from '@/modules/tutoring/shared/types';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, TablePagination, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Cancel, HelpOutline } from '@mui/icons-material';
import { useGetList } from 'react-admin';
import { useState } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';

const STATUSES = ['Present', 'Absent', 'Excused'];

const STATUS_COLORS: Record<string, ChipColor> = {
  Present: 'success',
  Absent: 'error',
  Excused: 'warning',
};

const STATUS_ICONS: Record<string, typeof CheckCircle> = {
  Present: CheckCircle,
  Absent: Cancel,
  Excused: HelpOutline,
};

export const AttendanceList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const { data: allAttendance } = useGetList('attendance', { pagination: { page: 1, perPage: 1000 }, filter: filters });
  const { data: allStudents } = useGetList('students', { pagination: { page: 1, perPage: 1000 } });
  const { data: allSessions } = useGetList('tutoring-sessions', { pagination: { page: 1, perPage: 1000 } });

  const attendance = allAttendance?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const studentMap = new Map((allStudents ?? []).map((s: StudentRecord) => [s.id, s]));
  const sessionMap = new Map((allSessions ?? []).map((s: SessionRecord) => [s.id, s]));

  const resolveStudentName = (id?: string) => {
    if (!id) return '—';
    const student = studentMap.get(id);
    const first = student?.legalEntity?.firstName ?? '';
    const last = student?.legalEntity?.lastName ?? '';
    const name = `${first} ${last}`.trim();
    return name || id;
  };

  const resolveSessionDate = (id?: string) => {
    if (!id) return '—';
    const session = sessionMap.get(id);
    if (!session?.startDateTime) return id;
    try {
      const d = new Date(session.startDateTime);
      return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return id;
    }
  };

  const formatDuration = (mins?: number) => {
    if (!mins) return '—';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const stats = {
    total: allAttendance?.length ?? 0,
    present: allAttendance?.filter((a: AttendanceRecord) => a.present).length ?? 0,
    absent: allAttendance?.filter((a: AttendanceRecord) => !a.present).length ?? 0,
    excused: 0,
  };

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Attendance"
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Present', value: String(stats.present), color: 'success.main' },
          { label: 'Absent', value: String(stats.absent), color: 'error.main' },
          { label: 'Excused', value: String(stats.excused), color: 'warning.main' },
        ]}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Student</InputLabel>
          <Select
            value={filters.studentId || ''}
            label="Student"
            onChange={e => setFilters(prev => ({ ...prev, studentId: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {allStudents?.map((s: StudentRecord) => <MenuItem key={s.id} value={s.id}>{s.legalEntity?.firstName ?? ""} {s.legalEntity?.lastName ?? ""}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Session</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attendance?.map((record: AttendanceRecord) => (
              <TableRow
                key={record.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/attendance/${record.id}/show`)}
              >
                <TableCell><Typography variant="body2" fontWeight={500}>{resolveStudentName(record.student?.id)}</Typography></TableCell>
                <TableCell><Typography variant="body2">{resolveSessionDate(record.session?.id)}</Typography></TableCell>
                <TableCell><Typography variant="body2">{formatDuration(record.durationMinutes)}</Typography></TableCell>
                <TableCell>
                  <Chip
                    icon={record.present ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                    label={record.present ? 'Present' : 'Absent'}
                    size="small"
                    color={record.present ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">—</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allAttendance?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!attendance || attendance.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No attendance records found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
